import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Newspaper, Pin, Plus } from "lucide-react";
import { listNews, getNewsPost, createNewsPost, updateNewsPost, deleteNewsPost, reorderNews, type NewsPostFull } from "@/lib/api";
import { validateNewsIndex, validateNewsPost, validateNewsRewards, type NewsIndex, type NewsIndexEntry, type NewsIssue } from "@/lib/news-schema";
import { useDirtyStore } from "@/lib/dirty-store";
import { cn } from "@/lib/utils";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import { NewsEditor } from "./NewsEditor";
import { GROUP_LABEL, GROUP_ORDER, blankDraft, byDisplayOrder, draftFromPost, entryFromDraft, lifecycleGroup, postFromDraft, type Draft, type Group } from "./news-model";
import "./news.css";

export function NewsWorkspace() {
  const qc = useQueryClient();
  const readOnly = usePreviewReadOnly();
  const setDirtyGlobal = useDirtyStore(s => s.setDirty);

  const listQuery = useQuery({ queryKey: ["news"], queryFn: listNews });
  const parsedIndex: NewsIndex = listQuery.data ?? [];

  const [selectedId, setSelectedId] = useState<string | "new" | null>(null);
  const selectedEntry = useMemo(() => (selectedId && selectedId !== "new" ? parsedIndex.find(e => e.id === selectedId) ?? null : null), [selectedId, parsedIndex]);
  const postQuery = useQuery({ queryKey: ["news", selectedEntry?.id], queryFn: () => getNewsPost(selectedEntry!.id), enabled: !!selectedEntry });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  // Seed the draft once the selection is known and, for an existing post, its body has loaded.
  useEffect(() => {
    if (selectedId === "new") {
      if (seededFor !== "new") {
        setDraft(blankDraft(parsedIndex.reduce((m, e) => Math.max(m, e.order), -1) + 1));
        setSeededFor("new");
      }
      return;
    }
    if (selectedEntry && postQuery.isSuccess && postQuery.data && seededFor !== selectedEntry.id) {
      setDraft(draftFromPost(postQuery.data));
      setSeededFor(selectedEntry.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedEntry, postQuery.isSuccess, postQuery.data]);

  const original = selectedEntry ?? null;
  const originalPost = postQuery.data ?? null;

  const { issues, dirty } = useMemo(() => {
    if (!draft) return { issues: [] as NewsIssue[], dirty: false };
    const entry = entryFromDraft(draft);
    const idx = parsedIndex.findIndex(e => e.id === (original?.id ?? ""));
    const nextIndex = idx >= 0 ? parsedIndex.map((e, i) => (i === idx ? entry : e)) : [...parsedIndex, entry];
    const targetIdx = idx >= 0 ? idx : nextIndex.length - 1;
    const indexIssues = validateNewsIndex(nextIndex).filter(i => i.path === `[${targetIdx}]` || i.path.startsWith(`[${targetIdx}].`));
    const bodyIssues = validateNewsPost({ id: draft.id, bodies: draft.bodies }, draft.id);
    const rewardIssues = validateNewsRewards(draft.rewards);
    const isDirty = selectedId === "new" ||
      JSON.stringify(entry) !== JSON.stringify(original) ||
      JSON.stringify(draft.bodies) !== JSON.stringify(originalPost?.bodies ?? {}) ||
      JSON.stringify(draft.rewards) !== JSON.stringify(originalPost?.rewards ?? []);
    return { issues: [...indexIssues, ...bodyIssues, ...rewardIssues], dirty: isDirty };
  }, [draft, parsedIndex, original, originalPost, selectedId]);

  const errors = issues.filter(i => i.severity === "error");
  // Missing-Indonesian fallbacks are expected, so they are not surfaced as warnings.
  const warnings = issues.filter(i => i.severity === "warning" && !/(^|\.)(titles|bodies)\.id$/.test(i.path));
  const canSave = !!draft && dirty && errors.length === 0;

  useEffect(() => { setDirtyGlobal(dirty); return () => setDirtyGlobal(false); }, [dirty, setDirtyGlobal]);
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const savingRef = useRef(false);
  const isNew = selectedId === "new";

  function selectPost(id: string | "new" | null) {
    if (savingRef.current) return;
    if (dirty && !window.confirm("Discard unsaved changes to this post?")) return;
    saveMutation.reset();
    setSeededFor(null);
    setDraft(null);
    setSelectedId(id);
  }

  const saveMutation = useMutation({
    mutationFn: async (post: NewsPostFull) => { await (isNew ? createNewsPost(post) : updateNewsPost(post.id, post)); return post; },
    onSuccess: async post => {
      qc.setQueryData(["news", post.id], post);
      qc.setQueryData<NewsIndex>(["news"], (index = []) => {
        const entry = entryFromDraft(draftFromPost(post));
        return index.some(item => item.id === post.id) ? index.map(item => item.id === post.id ? entry : item) : [...index, entry];
      });
      setSelectedId(post.id);
      setSeededFor(post.id);
      setDraft(draftFromPost(post));
      await qc.invalidateQueries({ queryKey: ["news"] });
    },
    onSettled: () => { savingRef.current = false; },
  });

  function handleSave() {
    if (!draft || !canSave || readOnly || savingRef.current || deleteMutation.isPending) return;
    savingRef.current = true;
    saveMutation.mutate(postFromDraft(draft));
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNewsPost(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["news"] });
      setSelectedId(null);
      setDraft(null);
      setSeededFor(null);
    },
  });

  function handleDelete() {
    if (!original || readOnly || savingRef.current) return;
    const title = original.titles.en ?? original.id;
    if (!window.confirm(`Delete "${title}"? This removes the post from D1 and cannot be undone here.`)) return;
    deleteMutation.mutate(original.id);
  }

  const reorderMutation = useMutation({
    mutationFn: (order: Array<{ id: string; order: number }>) => reorderNews(order),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["news"] }),
  });

  const [dragId, setDragId] = useState<string | null>(null);
  function handleDropOn(targetId: string, group: Group, now: number) {
    if (readOnly || savingRef.current || !dragId || dragId === targetId) return;
    const groupIds = parsedIndex.filter(e => lifecycleGroup(e, now) === group).sort(byDisplayOrder).map(e => e.id);
    const from = groupIds.indexOf(dragId), to = groupIds.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const reordered = [...groupIds];
    reordered.splice(from, 1);
    reordered.splice(to, 0, dragId);
    reorderMutation.mutate(reordered.map((id, i) => ({ id, order: i })));
    setDragId(null);
  }

  const now = Date.now();
  const grouped = useMemo(() => {
    const out: Record<Group, NewsIndexEntry[]> = { pinned: [], published: [], scheduled: [], draft: [], expired: [] };
    for (const e of parsedIndex) out[lifecycleGroup(e, now)].push(e);
    for (const g of GROUP_ORDER) out[g].sort(byDisplayOrder);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedIndex]);

  const canDrag = !saveMutation.isPending && !readOnly;
  const busy = listQuery.isFetching || reorderMutation.isPending;

  return <WorkspaceShell crumbs={[{ label: "Game configuration" }, { label: "News" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className={cn("ws-page nw-page", draft && "has-selection")}>
      <div className="ws-head">
        <h1>News</h1>
        {busy && <span className="pw-loading-dot" role="status" aria-label={reorderMutation.isPending ? "Saving order" : "Refreshing"} />}
        <div className="ws-head-actions nw-head-actions">
          <button type="button" className="pw-button pw-button-primary" onClick={() => selectPost("new")}><Plus size={14} />New post</button>
        </div>
      </div>

      {listQuery.isLoading && <div className="pw-empty"><span className="pw-loading-dot" aria-label="Loading news" /></div>}
      {listQuery.isError && <p className="ws-alert" role="alert">Couldn't load news: {String(listQuery.error)}</p>}
      {reorderMutation.isError && <p className="ws-alert" role="alert">Reorder failed: {String(reorderMutation.error)}</p>}

      {listQuery.isSuccess && <div className="nw-layout">
        <nav className="nw-list" aria-label="News posts">
          {parsedIndex.length === 0 && <p className="ws-empty">No posts yet</p>}
          {GROUP_ORDER.map(g => grouped[g].length === 0 ? null : <div key={g} className="nw-group">
            <h3>{GROUP_LABEL[g]}<span>{grouped[g].length}</span></h3>
            {grouped[g].map(e => <div key={e.id} role="button" tabIndex={0} aria-pressed={selectedId === e.id} draggable={canDrag}
              className={cn("nw-option", selectedId === e.id && "is-active", dragId === e.id && "is-dragging")}
              onDragStart={() => setDragId(e.id)} onDragEnd={() => setDragId(null)} onDragOver={ev => ev.preventDefault()} onDrop={() => handleDropOn(e.id, g, now)}
              onClick={() => selectPost(e.id)} onKeyDown={ev => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); selectPost(e.id); } }}>
              {canDrag && <GripVertical size={13} className="nw-grip" aria-hidden="true" />}
              <span className="nw-option-text"><strong>{e.titles.en || <em>(untitled)</em>}</strong><small>{e.id}</small></span>
              {e.pinned && <Pin size={12} className="nw-option-pin" aria-label="Pinned" />}
            </div>)}
          </div>)}
        </nav>

        <div className="nw-content">
          {!draft ? (selectedEntry && postQuery.isError
            ? <p className="ws-alert" role="alert">Couldn't load post: {String(postQuery.error)}</p>
            : isNew || (selectedEntry && postQuery.isPending) ? <div className="pw-empty"><span className="pw-loading-dot" aria-label="Loading post" /></div>
            : <div className="pw-empty nw-placeholder"><Newspaper size={22} /><span>Select a post</span></div>)
            : <fieldset className="nw-fieldset" disabled={saveMutation.isPending || deleteMutation.isPending}>
              <NewsEditor draft={draft} setDraft={setDraft} isNew={isNew} errors={errors} warnings={warnings} canSave={canSave} onSave={handleSave}
                saving={saveMutation.isPending} onDelete={!isNew ? handleDelete : undefined} deleting={deleteMutation.isPending} readOnly={readOnly} onBack={() => selectPost(null)} />
              {saveMutation.error && <p className="ws-alert" role="alert">{String(saveMutation.error)}</p>}
              {deleteMutation.error && <p className="ws-alert" role="alert">{String(deleteMutation.error)}</p>}
              {saveMutation.isSuccess && !dirty && <p className="ws-alert is-ok" role="status">Changes saved.</p>}
            </fieldset>}
        </div>
      </div>}
    </div></div>
  </WorkspaceShell>;
}
